import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface PDFOptions {
  size?: "A4" | "half-A4" | "half-A4-horizontal";
  orientation?: "p" | "l";
  fontName?: string;
}

export const printPDF = (
  headers: string[],
  lines: string[],
  columns: string[],
  isCancelled?: boolean,
  rows: (string | number)[][] = [],
  options: PDFOptions = {}
): void => {
  const {
    size = "A4",
    orientation = "p",
    fontName = "helvetica",
  } = options;

  // ===== PAGE SIZE =====
  let format: [number, number] | string = "a4";
  if (size === "half-A4") format = [148, 210];
  else if (size === "half-A4-horizontal") format = [210, 148];

  const doc = new jsPDF({ orientation, unit: "mm", format });
  doc.setFont(fontName);

  const pageWidth =
    typeof format === "string"
      ? doc.internal.pageSize.getWidth()
      : format[0];

  const margin = 12;
  const usableWidth = pageWidth - margin * 2;

  let y = 20;

  // ===== HEADER =====
  if (headers.length) {
    doc.setFontSize(12);
    doc.setFont(fontName, "bold");
    const centerX = pageWidth / 2;
    headers.forEach(text => {
      doc.text(text, centerX, y, { align: "center" });
      y += 6;
    });
  }

  // ===== INFO LINES =====
  if (lines.length) {
    y += 2;
    doc.setFontSize(10);
    doc.setFont(fontName, "normal");
    lines.forEach(line => {
      doc.text(line, margin, y);
      y += 5;
    });
  }

  // ===== COLUMN WIDTH CALC =====
  const calculateColumnWidths = (
    cols: string[],
    data: (string | number)[][]
  ) => {
    const fontSize = 9;
    const charWidth = fontSize * 0.25;
    const padding = 4;

    const MAX_CHARS_FOR_WIDTH = 35;
    const MIN_COL_WIDTH = 16;

    let rawWidths = cols.map((col, i) => {
      const headerWidth = col.length * charWidth;

      const dataWidth = Math.max(
        ...((Array.isArray(data) ? data : []).map(row => {
          const len = String(row[i] ?? "").length;
          return Math.min(len, MAX_CHARS_FOR_WIDTH) * charWidth;
        })),
        0
      );
      return Math.max(
        Math.max(headerWidth, dataWidth) + padding,
        MIN_COL_WIDTH
      );
    });

    const totalWidth = rawWidths.reduce((a, b) => a + b, 0);
    const scale = usableWidth / totalWidth;

    return rawWidths.map(w => w * scale);
  };

  const columnWidths = calculateColumnWidths(columns, rows);

  // ===== TABLE =====
  autoTable(doc, {
    startY: y + 4,
    head: [columns],
    body: rows,
    theme: "plain",
    margin: { left: margin, right: margin, top: 20, bottom: 25 },
    styles: {
      fontSize: 9,
      cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 },
      overflow: "linebreak",
      cellWidth: "wrap",
      valign: "top",
      textColor: 0,
    },
    headStyles: {
      fontStyle: "bold",
      lineWidth: { top: 0.2, bottom: 0.2 },
      lineColor: [0, 0, 0],
    },
    columnStyles: columnWidths.reduce(
      (acc, width, index) => ({
        ...acc,
        [index]: { cellWidth: width },
      }),
      {}
    ),
  });

  // ===== WATERMARK FUNCTION =====
  const addWatermark = () => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.saveGraphicsState();
    //doc.setTextColor(220, 220, 220);
    doc.setTextColor(255, 150, 150)
    doc.setFont(fontName, "regular");
    doc.setFontSize(35);
    const offsetY = 22;
    doc.text("CANCELLED", pageWidth / 2, pageHeight / 2 + offsetY, {
      align: "center",
      angle: 30,
    });

    doc.restoreGraphicsState();
  };

  // ===== APPLY WATERMARK TO ALL PAGES =====
  if (isCancelled) {
    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addWatermark();
    }
  }

  // ===== FINAL LINE =====
  const finalY = (doc as any).lastAutoTable.finalY + 1;
  doc.line(margin, finalY, pageWidth - margin, finalY);

  doc.autoPrint();

  // ===== OPEN PDF =====
  window.open(doc.output("bloburl"), "_blank");
};